import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { offerUndo } from "_shared/_providers/snackbars/undo-snackbar";
import { api } from "_shared/_stores/api";
import AHDate from "_shared/dates/date";
import Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import {
  type LoweredNotificationsJSON,
  useAddPermissionMutation,
  useDisableGranteePermissionsMutation,
  useDisablePermissionMutation,
  useEnablePermissionMutation,
  useRegrantPermissionsMutation,
  useUpdatePermissionMutation,
} from "permissions/_api/permissions-api-service";
import type { PermissionJSON } from "permissions/_schemas/permission-schema";
import { grantRowId } from "permissions/_views/grant-row/grant-row-model";
import { fieldCounts } from "permissions/_views/people/person-record-grants-model";
import type { FieldAccessValues } from "permissions/_views/people/field-access-dialog";
import {
  CHANGED_BY_SOMEONE_ELSE,
  type GrantLevel,
  changeAnnouncements,
  fieldSummary,
  levelLabel,
} from "permissions/_views/wording/permission-wording";
import Permission from "permissions/permission";
import { resourceFromJSON } from "permissions/resources/permission-resource-factory";
import type { Resource } from "permissions/resources/permission-resource";
import type { AnyResourceJSON } from "permissions/resources/permission-resource-types";
import { CHANGE_HIGHLIGHT_DURATION_MS } from "_shared/_views/highlights/change-highlight-tint";
import { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

/** How long a removal's Undo is offered while nobody points at it or has focus in it. */
export const REMOVAL_UNDO_MS = 7500;

/**
 * The resource a grant of `kind` at `level` carries: the kind's containment (its resource type and
 * discriminator, spelled as the server stores them), then the level. A level part the kind has no
 * control for is the kind's default — signing authority is always Read/Write at Entity-Wide — and a
 * part the default leaves null is left out, as own sign-in has no level at all.
 */
export function resourceForKind(kind: PermissionKind, level?: GrantLevel): Resource {
  const hasControl = (name: string) => kind.controls.some((control) => control.name === name);
  const accessLevel = hasControl("accessLevel") ? (level?.accessLevel ?? kind.default?.accessLevel) : kind.default?.accessLevel;
  const scope = hasControl("scope") ? (level?.scope ?? kind.default?.scope) : kind.default?.scope;
  const json: Record<string, unknown> = { ...kind.containment };
  if (accessLevel !== null && accessLevel !== undefined) json.accessLevel = accessLevel;
  if (scope !== null && scope !== undefined) json.scope = scope;
  return resourceFromJSON(json as AnyResourceJSON);
}

/** The same grant at another level: only the level and scope change, never what it is a grant of. */
export function withLevel(permission: Permission, level: GrantLevel): Permission {
  const json = { ...(permission.resource.toJSON() as Record<string, unknown>) };
  if (level.accessLevel !== undefined) json.accessLevel = level.accessLevel;
  if (level.scope !== undefined) json.scope = level.scope;
  return new Permission({
    id: permission.id,
    resource: resourceFromJSON(json as AnyResourceJSON),
    granteeUserId: permission.granteeUserId,
    grantorUserId: permission.grantorUserId,
    entityId: permission.entityId,
    note: permission.note,
    version: permission.version,
  });
}

/**
 * The same per-field grant conferring exactly `fields`: a field left out is a field not granted. Only
 * the fields change, never the record the grant is on.
 */
export function withFields(permission: Permission, fields: FieldAccessValues): Permission {
  const json = { ...(permission.resource.toJSON() as Record<string, unknown>), fields: { ...fields } };
  return new Permission({
    id: permission.id,
    resource: resourceFromJSON(json as AnyResourceJSON),
    granteeUserId: permission.granteeUserId,
    grantorUserId: permission.grantorUserId,
    entityId: permission.entityId,
    note: permission.note,
    version: permission.version,
  });
}

/**
 * The grant a re-grant minted, as this page can draw it before any list is read again: what the
 * removed grant was a grant of, under the new id and version the server answered with (#6668).
 */
export function regrantedGrant(removed: Permission, answer: Readonly<Partial<PermissionJSON>>): Permission | undefined {
  if (!answer.id) return undefined;
  return new Permission({
    id: new Guid(answer.id),
    resource: removed.resource,
    granteeUserId: removed.granteeUserId,
    // A grant the system derived is re-granted as the system's, with the empty grantor every read of
    // a grant accepts; building it as a person's id threw after the server had granted it.
    grantorUserId: Guid.fromJSON(answer.grantorUserId, { allowEmpty: true }) ?? removed.grantorUserId,
    entityId: removed.entityId,
    note: answer.note ?? removed.note,
    version: answer.version ?? 0,
    createdDate: AHDate.fromJSON(answer.createdDate) ?? undefined,
  });
}

function isConflict(error: unknown): boolean {
  return (error as FetchBaseQueryError | undefined)?.status === HttpStatusCode.Conflict;
}

/** How long focus follows a row's switch; the refetches after a change land well within it. */
export const FOLLOW_ROW_FOCUS_MS = 5000;

/**
 * Focuses a row's switch once the row is drawn, and again each time the row is drawn afresh while
 * focus has been left on nothing. A re-granted row is a new row with a new id, and the page's lists
 * refetch one at a time after it: when the removed list lands before the live one, neither holds the
 * new grant for a moment, so the removed row is drawn again and the new one is dropped and redrawn,
 * taking focus with it. Focus the reader has moved anywhere else is theirs, and stops the following.
 */
export function focusRowSwitch(rowId: string): void {
  const switchOf = () =>
    document.getElementById(rowId)?.querySelector<HTMLInputElement>("input[role='switch'], input[type='checkbox']") ?? null;
  let placed = false;
  const place = () => {
    const input = switchOf();
    if (!input || input === document.activeElement) return;
    // A dropped row leaves focus on the body; anywhere else, something put it there on purpose.
    if (placed && document.activeElement !== null && document.activeElement !== document.body) return;
    placed = true;
    input.focus();
  };
  const observer = new MutationObserver(place);
  const onFocusIn = (event: FocusEvent) => {
    if (event.target !== switchOf()) stop();
  };
  const timer = setTimeout(() => stop(), FOLLOW_ROW_FOCUS_MS);
  function stop() {
    observer.disconnect();
    document.removeEventListener("focusin", onFocusIn);
    clearTimeout(timer);
  }
  place();
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener("focusin", onFocusIn);
}

/**
 * How one change is told. A change made to many rows at once is told once, by the page, so each
 * row's change is `quiet`: it is announced in the live region and drawn on its row, but raises no
 * snackbar and offers no snackbar Undo. The row's switch is still the undo.
 */
export type GrantChangeOptions = Readonly<{ quiet?: boolean }>;

export type GrantChanges = Readonly<{
  /** The row keys — a grant's id, or `kind-{slug}` for a row with none — whose change is unanswered. */
  pending: ReadonlySet<string>;
  /** What went wrong with a row's last change, said under it. */
  alerts: ReadonlyMap<string, string>;
  /** The grant ids just changed here, drawn with the change tint for a moment. */
  highlighted: ReadonlySet<string>;
  /** The grant ids removed from this page, which read "Removed by you just now" until navigation. */
  removedJustNow: ReadonlySet<string>;
  /**
   * The grants removed from this page, as the server answered, by the id they were removed under.
   * A page that reads only live grants draws these in place, so a removed row stays where it was.
   */
  removed: ReadonlyMap<string, Permission>;
  /**
   * The grants granted again from this page, as the server answered, by their new id. A re-grant mints
   * a new grant, and a page's lists can be read back one at a time and before the new grant is in any
   * of them, so a page draws these until a list holds them (#6668).
   */
  regranted: ReadonlyMap<string, Permission>;
  /** The live region's last words, keyed so the same sentence twice is announced twice. */
  announcement: Readonly<{ key: number; text: string }>;
  /** Grants `kind` at `level`, or exactly `resource` when given: a per-record grant names its record. */
  grant: (kind: PermissionKind, level: GrantLevel | undefined, granteeUserId: Guid, resource?: Resource) => Promise<boolean>;
  changeLevel: (permission: Permission, kind: PermissionKind, level: GrantLevel) => Promise<boolean>;
  /** Sets the fields one proposal or one work agreement confers. */
  changeFields: (permission: Permission, kind: PermissionKind, fields: FieldAccessValues) => Promise<boolean>;
  remove: (permission: Permission, kind: PermissionKind, options?: GrantChangeOptions) => Promise<boolean>;
  grantAgain: (permission: Permission, kind: PermissionKind) => Promise<boolean>;
  /**
   * "Remove all access…" for one person: every grant but notifications, and own sign-in when
   * `stopSignIn`; organization-wide notifications are lowered to the person's own. Resolves true once
   * the server has answered; its Undo grants the set again, raises the notifications back, and then
   * calls `after.onRestored`.
   */
  removeAll: (granteeUserId: Guid, stopSignIn: boolean, after?: RemoveAllFollowUp) => Promise<boolean>;
}>;

/** What the page does around Remove all access's Undo: where focus goes, and what to show after. */
export type RemoveAllFollowUp = Readonly<{ returnFocus?: () => void; onRestored?: () => void }>;

/** The pending key of "Remove all access…", which is no one row's. */
export const REMOVE_ALL_KEY = "remove-all";

export type GrantChangeSubject = Readonly<{
  legalEntityId: Guid;
  /** The name a grant's holder is called by in what the page says about the change. */
  nameOf: (granteeUserId: Guid) => string;
}>;

function without<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  next.delete(value);
  return next;
}

function withoutKey<K, V>(map: ReadonlyMap<K, V>, key: K): Map<K, V> {
  const next = new Map(map);
  next.delete(key);
  return next;
}

/**
 * The grant rows' writes, and what the reader is told about each (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "Adding, removing, changing"). The person page changes one person's
 * grants; the kind page changes one kind's grants for many people, so each change names its own
 * holder through `nameOf`.
 *
 * - **Grant** is a `POST`; **change level** a `PUT` on the row that holds it; **remove** is
 *   `DELETE /permissions/{id}`, which disables the grant and withdraws the derived grants it alone
 *   justified; **grant again** is the re-grant, which mints a new grant whose id replaces the old one.
 *   The API service writes each answer into the cached lists before its refetch lands, so a row
 *   changes in place.
 * - A row moves enabled → removing (pending) → removed ("Removed by you just now", its level
 *   read-only) → granted again (re-keyed to the new id). The switch is the primary undo and has no
 *   time limit; the snackbar's Undo is the second way, for 7.5 seconds that wait while it is pointed
 *   at or focused, and it sends focus back to the row's switch.
 * - A removal withdraws the derived grants that rested only on the removed one, and the re-grant
 *   restores them (#6189), so every removal offers its Undo without asking first. The snackbar and
 *   the live region say how many derived grants each one took or brought back.
 * - Every write sends the row's version. A 409 refetches the lists, leaves the control as the server
 *   has it, and says "Changed by someone else, reloaded" under the row; nothing is retried. Any other
 *   failure is the server's message in a snackbar.
 * - Each success is announced once in the page's polite live region, and the row is tinted.
 */
export function useGrantChanges(subject: GrantChangeSubject, viewerUserId: Guid): GrantChanges {
  const { legalEntityId, nameOf } = subject;
  const dispatch = useDispatch();
  const [addPermission] = useAddPermissionMutation();
  const [updatePermission] = useUpdatePermissionMutation();
  const [disablePermission] = useDisablePermissionMutation();
  const [enablePermission] = useEnablePermissionMutation();
  const [disableGranteePermissions] = useDisableGranteePermissionsMutation();
  const [regrantPermissions] = useRegrantPermissionsMutation();
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());
  const [alerts, setAlerts] = useState<ReadonlyMap<string, string>>(new Map());
  const [highlighted, setHighlighted] = useState<ReadonlySet<string>>(new Set());
  const [removed, setRemoved] = useState<ReadonlyMap<string, Permission>>(new Map());
  const [regrantedHere, setRegrantedHere] = useState<ReadonlyMap<string, Permission>>(new Map());
  // How many derived grants each removal here withdrew, by the key its row is removed under, so every
  // way back — the snackbar's Undo and the row's switch alike — can say when they did not come back.
  const [withdrawnBy, setWithdrawnBy] = useState<ReadonlyMap<string, number>>(new Map());
  const [announcement, setAnnouncement] = useState({ key: 0, text: "" });

  // Memoized on purpose: the person page filters its rows on it, and a fresh set each render would
  // refilter them each render.
  const removedJustNow = useMemo<ReadonlySet<string>>(() => new Set(removed.keys()), [removed]);

  const announce = useCallback((text: string) => setAnnouncement((current) => ({ key: current.key + 1, text })), []);

  const highlight = useCallback((id: string | undefined) => {
    if (!id) return;
    setHighlighted((current) => new Set(current).add(id));
    setTimeout(() => setHighlighted((current) => without(current, id)), CHANGE_HIGHLIGHT_DURATION_MS);
  }, []);

  /**
   * Runs one write for one row: marks it pending, clears what was said about its last change, and on
   * failure says what went wrong. Resolves to the answer, or `undefined` when there was none.
   */
  const track = useCallback(
    async <T>(key: string, failure: string, change: () => Promise<T>): Promise<T | undefined> => {
      setPending((current) => new Set(current).add(key));
      setAlerts((current) => withoutKey(current, key));
      let answer: T | undefined;
      let error: unknown;
      try {
        answer = await change();
      } catch (caught) {
        error = caught;
      }
      setPending((current) => without(current, key));
      if (error === undefined) return answer;

      if (isConflict(error)) {
        // Someone else changed the grant since it was read. The lists are read again, so the control
        // shows what the server holds now; nothing is sent again on the reader's behalf.
        dispatch(api.util.invalidateTags([{ type: "Permission", id: "LIST" }]));
        // "Remove all access…" and its Undo are no one row's, so no row would draw the alert: it is
        // said in a snackbar instead, or the reader would see nothing happen.
        if (key === REMOVE_ALL_KEY) enqueueSnackbar(`${failure} ${CHANGED_BY_SOMEONE_ELSE}.`, { variant: "warning" });
        else setAlerts((current) => new Map(current).set(key, CHANGED_BY_SOMEONE_ELSE));
      } else {
        enqueueSnackbar(getErrorMessageFromUnknown(error) ?? failure, { variant: "error" });
      }
      return undefined;
    },
    [dispatch],
  );

  const grant = useCallback(
    async (kind: PermissionKind, level: GrantLevel | undefined, granteeUserId: Guid, resource?: Resource) => {
      const name = nameOf(granteeUserId);
      const granted = await track(`kind-${kind.slug}`, `Could not grant ${kind.name} to ${name}.`, () =>
        addPermission({
          permission: new Permission({
            resource: resource ?? resourceForKind(kind, level),
            granteeUserId,
            grantorUserId: viewerUserId,
            entityId: legalEntityId,
            version: 0,
          }),
        }).unwrap(),
      );
      if (!granted) return false;
      highlight(granted.id ?? undefined);
      announce(changeAnnouncements.granted(kind.name, name));
      return true;
    },
    [addPermission, announce, highlight, legalEntityId, nameOf, track, viewerUserId],
  );

  const changeLevel = useCallback(
    async (permission: Permission, kind: PermissionKind, level: GrantLevel) => {
      const key = permission.id?.value ?? `kind-${kind.slug}`;
      const name = nameOf(permission.granteeUserId);
      const changed = await track(key, `Could not change ${name}'s ${kind.name}.`, () =>
        updatePermission({ permission: withLevel(permission, level) }).unwrap(),
      );
      if (!changed) return false;
      highlight(changed.id ?? undefined);
      announce(changeAnnouncements.changed(kind.name, name, levelLabel(level)));
      return true;
    },
    [announce, highlight, nameOf, track, updatePermission],
  );

  const changeFields = useCallback(
    async (permission: Permission, kind: PermissionKind, fields: FieldAccessValues) => {
      const key = permission.id?.value ?? `kind-${kind.slug}`;
      const name = nameOf(permission.granteeUserId);
      const updated = withFields(permission, fields);
      const changed = await track(key, `Could not change ${name}'s ${kind.name}.`, () =>
        updatePermission({ permission: updated }).unwrap(),
      );
      if (!changed) return false;
      const counts = fieldCounts(updated);
      highlight(changed.id ?? undefined);
      announce(changeAnnouncements.changed(kind.name, name, fieldSummary(counts.granted, counts.total, counts.writable)));
      return true;
    },
    [announce, highlight, nameOf, track, updatePermission],
  );

  /**
   * Grants a removed grant again, and answers with the new grant's id, which its row is keyed by.
   * `withdrawn` is how many derived grants the removal took, when this page made it and knows.
   */
  const regrant = useCallback(
    async (permission: Permission, kind: PermissionKind, withdrawn = 0) => {
      const key = permission.id?.value ?? `kind-${kind.slug}`;
      const name = nameOf(permission.granteeUserId);
      const regranted = await track(key, `Could not grant ${kind.name} to ${name} again.`, () =>
        enablePermission({ permission }).unwrap(),
      );
      if (!regranted) return undefined;
      const minted = regrantedGrant(permission, regranted);
      const mintedId = minted?.id?.value;
      if (minted && mintedId) setRegrantedHere((current) => new Map(current).set(mintedId, minted));
      setRemoved((current) => withoutKey(current, key));
      setWithdrawnBy((current) => withoutKey(current, key));
      highlight(regranted.id ?? undefined);
      const restored = regranted.restoredPermissions;
      if (restored === undefined && withdrawn > 0) {
        // A server from before #6189 granted it again and restored nothing it withdrew. Say so,
        // rather than let the Undo read as having undone the whole removal.
        const message = changeAnnouncements.grantedAgainWithoutRestoring(kind.name, name, withdrawn);
        enqueueSnackbar(message, { variant: "warning", role: "alert" });
        announce(message);
        return regranted.id ?? undefined;
      }
      for (const row of restored ?? []) highlight(row.id ?? undefined);
      announce(changeAnnouncements.grantedAgain(kind.name, name, restored?.length ?? 0));
      return regranted.id ?? undefined;
    },
    [announce, enablePermission, highlight, nameOf, track],
  );

  const grantAgain = useCallback(
    async (permission: Permission, kind: PermissionKind) => {
      const withdrawn = withdrawnBy.get(permission.id?.value ?? `kind-${kind.slug}`) ?? 0;
      return (await regrant(permission, kind, withdrawn)) !== undefined;
    },
    [regrant, withdrawnBy],
  );

  /**
   * Removes a grant, and offers the Undo that grants it again with what it withdrew. A `quiet`
   * removal is one of many the page tells once, so it raises no snackbar of its own.
   */
  const remove = useCallback(
    async (permission: Permission, kind: PermissionKind, options?: GrantChangeOptions) => {
      const key = permission.id?.value ?? `kind-${kind.slug}`;
      const name = nameOf(permission.granteeUserId);
      const disabled = await track(key, `Could not remove ${name}'s ${kind.name}.`, () =>
        disablePermission({ permission }).unwrap(),
      );
      if (!disabled) return false;

      const withdrawn = disabled.withdrawnPermissions.length;
      const message = changeAnnouncements.removed(kind.name, name, withdrawn);
      // The removed row is what Undo re-grants: its version is the one the removal left.
      const removedGrant = Permission.fromJSON(disabled);
      setRemoved((current) => new Map(current).set(key, removedGrant));
      setRegrantedHere((current) => withoutKey(current, key));
      setWithdrawnBy((current) => new Map(current).set(key, withdrawn));
      highlight(disabled.id ?? undefined);
      announce(message);
      if (options?.quiet) return true;

      const rowId = grantRowId(kind, removedGrant);
      offerUndo({
        message,
        durationMs: REMOVAL_UNDO_MS,
        role: "status",
        returnFocus: () => focusRowSwitch(rowId),
        undo: () => {
          void regrant(removedGrant, kind, withdrawn).then((newId) => {
            // Granted again is a new grant with a new id, so a new row; focus follows it there.
            if (newId) focusRowSwitch(`grant-${newId}`);
          });
        },
      });
      return true;
    },
    [announce, disablePermission, highlight, nameOf, regrant, track],
  );

  /** Grants the removed set again in one call, the Undo of removing all access. */
  const restoreAll = useCallback(
    async (
      name: string,
      removedSet: readonly PermissionJSON[],
      lowered: LoweredNotificationsJSON | null | undefined,
      onRestored: (() => void) | undefined,
    ) => {
      const regranted = await track(REMOVE_ALL_KEY, `Could not grant ${name}'s access again.`, () =>
        regrantPermissions({ permissions: removedSet, notifications: lowered }).unwrap(),
      );
      if (!regranted) return;
      setRemoved((current) => {
        const next = new Map(current);
        for (const permission of removedSet) if (permission.id) next.delete(permission.id);
        return next;
      });
      for (const permission of regranted) highlight(permission.id ?? undefined);
      announce(changeAnnouncements.restoredAll(name));
      enqueueSnackbar(changeAnnouncements.restoredAll(name), { variant: "success", role: "status" });
      onRestored?.();
    },
    [announce, highlight, regrantPermissions, track],
  );

  const removeAll = useCallback(
    async (granteeUserId: Guid, stopSignIn: boolean, after?: RemoveAllFollowUp) => {
      const name = nameOf(granteeUserId);
      const removal = await track(REMOVE_ALL_KEY, `Could not remove ${name}'s access.`, () =>
        disableGranteePermissions({ granteeUserId, stopSignIn }).unwrap(),
      );
      if (!removal) return false;

      const lowered = removal.loweredNotifications;
      const message = changeAnnouncements.removedAll(name, removal.disabled.length, removal.withdrawn.length, Boolean(lowered));
      setRemoved((current) => {
        const next = new Map(current);
        for (const permission of removal.disabled) if (permission.id) next.set(permission.id, Permission.fromJSON(permission));
        return next;
      });
      for (const permission of removal.disabled) highlight(permission.id ?? undefined);
      highlight(lowered?.permission.id ?? undefined);
      announce(message);

      // The Undo re-grants the disabled set only: unlike one removal's re-grant (#6189), it does not
      // restore what the server withdrew beyond the set, so a removal that withdrew anything offers
      // no Undo. Lowered notifications are raised back.
      if ((removal.disabled.length > 0 || lowered) && removal.withdrawn.length === 0) {
        offerUndo({
          message,
          durationMs: REMOVAL_UNDO_MS,
          role: "status",
          returnFocus: () => after?.returnFocus?.(),
          undo: () => void restoreAll(name, removal.disabled, lowered, after?.onRestored),
        });
      } else {
        enqueueSnackbar(message, { variant: "success", role: "status" });
      }
      return true;
    },
    [announce, disableGranteePermissions, highlight, nameOf, restoreAll, track],
  );

  return {
    pending,
    alerts,
    highlighted,
    removedJustNow,
    removed,
    regranted: regrantedHere,
    announcement,
    grant,
    changeLevel,
    changeFields,
    remove,
    grantAgain,
    removeAll,
  };
}
